import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import AppsLayout from './AppsLayout';
import Reveal from './components/Reveal';
import { APPS_ROUTES } from './routes';
import { qrCode } from '../../content/qr-code/app';
import { support } from '../../content/support';
import { trackClick } from '../../firebase/analytics';

const PAGE = 'apps_qr_code';
const MAX_INPUT_LENGTH = 1000;
const QR_SIZE = 224;
const QR_LEVEL = 'M';
const QR_MARGIN_SIZE = 4;
const QR_FG = '#000000';
const QR_BG = '#ffffff';

function supportsCanvas() {
  if (typeof document === 'undefined') return false;
  try {
    const canvas = document.createElement('canvas');
    const context = typeof canvas.getContext === 'function' ? canvas.getContext('2d') : null;
    return !!context && typeof canvas.toDataURL === 'function';
  } catch (error) {
    return false;
  }
}

class QrCodeErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch() {
    this.props.onError();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

export default function QrCodePage() {
  const [text, setText] = useState('');
  const [generatedValue, setGeneratedValue] = useState('');
  const [status, setStatus] = useState({ tone: 'info', message: 'Enter text or a URL, then select Generate QR code.' });
  const downloadCanvasRef = useRef(null);
  const canvasSupported = supportsCanvas();

  const handleTextChange = (event) => {
    const nextValue = event.target.value;
    setText(nextValue);

    if (!generatedValue) return;
    const normalized = nextValue.trim();
    if (normalized !== generatedValue) {
      setStatus({ tone: 'info', message: 'Select Generate to update the code.' });
      return;
    }
    setStatus({ tone: 'success', message: 'QR code ready.' });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    // Privacy: analytics events for this tool are fixed IDs only; never include user input or derivatives.
    trackClick(PAGE, 'qr_generate_button')();

    const value = text.trim();
    if (!value) {
      setGeneratedValue('');
      setStatus({ tone: 'warning', message: 'Enter some text or a URL first.' });
      return;
    }

    if (value.length > MAX_INPUT_LENGTH) {
      setGeneratedValue('');
      setStatus({ tone: 'warning', message: `That input is too long. Please keep it to ${MAX_INPUT_LENGTH} characters or fewer.` });
      return;
    }

    setGeneratedValue(value);
    setStatus({ tone: 'success', message: 'QR code ready.' });
  };

  const handleDownloadClick = () => {
    // Privacy: analytics events for this tool are fixed IDs only; never include user input or derivatives.
    trackClick(PAGE, 'qr_download_button')();

    if (!generatedValue) {
      setStatus({ tone: 'warning', message: 'Generate a QR code before downloading.' });
      return;
    }

    if (!canvasSupported) {
      setStatus({ tone: 'warning', message: 'Download is unavailable in this browser.' });
      return;
    }

    try {
      const dataUrl = downloadCanvasRef.current?.toDataURL('image/png');
      if (!dataUrl) {
        setStatus({ tone: 'warning', message: 'Download is unavailable in this browser.' });
        return;
      }

      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = 'qr-code.png';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setStatus({ tone: 'success', message: 'Download ready.' });
    } catch (error) {
      setStatus({ tone: 'warning', message: 'Download is unavailable in this browser.' });
    }
  };

  const handleRenderError = () => {
    setGeneratedValue('');
    setStatus({ tone: 'warning', message: 'That content cannot be encoded as a QR code. Try a shorter value.' });
  };

  return (
    <AppsLayout
      title="QR code generator · Portfolio · Keval Langalia"
      description="A free QR code generator that runs entirely in your browser. Nothing you type is uploaded."
      canonicalPath={APPS_ROUTES.qrCode}
      breadcrumbs={[{ label: 'Portfolio', to: APPS_ROUTES.apps, id: 'apps' }, { label: qrCode.name }]}
      page={PAGE}
      support={false}
    >
      <Reveal as="section" className="apps-hero" aria-labelledby="qr-code-heading">
        <p className="apps-eyebrow">Portfolio · Web tool</p>
        <h1 id="qr-code-heading" className="apps-title">{qrCode.name}</h1>
        <p className="apps-lead">Create a high-contrast QR image instantly in your browser, then download it as a PNG.</p>
      </Reveal>

      <section className="apps-section" aria-labelledby="qr-code-generator-heading">
        <h2 id="qr-code-generator-heading" className="apps-section-title">Generate</h2>
        <div className="kds-pane apps-qr-tool">
          <form className="apps-enrollment-form" onSubmit={handleSubmit}>
            <label htmlFor="qr-code-text" className="apps-enrollment-label">Text or URL</label>
            <input
              id="qr-code-text"
              name="qrCodeText"
              className="apps-input"
              value={text}
              onChange={handleTextChange}
              maxLength={MAX_INPUT_LENGTH}
            />

            <div className="apps-enrollment-actions">
              <button type="submit" className="kds-btn kds-btn--filled">Generate QR code</button>
              <button type="button" className="kds-btn kds-btn--tonal" onClick={handleDownloadClick}>
                Download PNG
              </button>
            </div>
          </form>

          <div className={`apps-enrollment-status apps-enrollment-status--${status.tone}`} role="status" aria-live="polite">
            <p><strong>{status.message}</strong></p>
          </div>

          {generatedValue ? (
            <figure className="apps-qr-figure" role="img" aria-label={`QR code for ${generatedValue}`}>
              <QrCodeErrorBoundary onError={handleRenderError} resetKey={generatedValue}>
                <>
                  <QRCodeSVG
                    className="apps-qr-symbol"
                    value={generatedValue}
                    size={QR_SIZE}
                    level={QR_LEVEL}
                    marginSize={QR_MARGIN_SIZE}
                    fgColor={QR_FG}
                    bgColor={QR_BG}
                    title="Generated QR code"
                  />
                  {canvasSupported ? (
                    <QRCodeCanvas
                      ref={downloadCanvasRef}
                      className="kds-visually-hidden"
                      aria-hidden="true"
                      value={generatedValue}
                      size={QR_SIZE}
                      level={QR_LEVEL}
                      marginSize={QR_MARGIN_SIZE}
                      fgColor={QR_FG}
                      bgColor={QR_BG}
                    />
                  ) : null}
                </>
              </QrCodeErrorBoundary>
            </figure>
          ) : null}
          {generatedValue ? (
            <p className="apps-muted">Dark modules on a light background improve scan reliability.</p>
          ) : null}
        </div>
      </section>

      <section className="apps-section" aria-labelledby="qr-code-how-heading">
        <h2 id="qr-code-how-heading" className="apps-section-title">How this works</h2>
        <p className="apps-muted">
          QR generation and PNG export happen entirely in your browser tab. The text you enter stays local and is never sent to a server.
        </p>
        <p className="apps-muted">
          No sign-up, no upload, no limits. If it saved you a minute, you can{' '}
          <a href={support.href} target="_blank" rel="noopener noreferrer" onClick={trackClick(PAGE, support.element)}>buy me a coffee</a>.
        </p>
        <p className="kds-faint">QR Code is a registered trademark of DENSO WAVE INCORPORATED.</p>
        <p className="kds-faint">
          <Link to={APPS_ROUTES.qrCodeLicenses} className="kds-link" onClick={trackClick(PAGE, 'qr_licenses_link')}>
            Licenses & notices
          </Link>
        </p>
      </section>
    </AppsLayout>
  );
}
